import { useState, useEffect } from 'react'
import { X, Building2, Users, MessageSquare, ExternalLink, Save, Loader2, AlertTriangle } from 'lucide-react'
import { TenantSummary, updateTenantById } from '../../../services/api'
import { useTenant } from '../../../hooks/useTenant'
import AdminUserManager from './AdminUserManager'
import toast from 'react-hot-toast'

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  starter: 'bg-blue-100 text-blue-700',
  trial: 'bg-orange-100 text-orange-700',
  pro: 'bg-yellow-100 text-yellow-700',
  enterprise: 'bg-purple-100 text-purple-700',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-orange-100 text-orange-700',
  suspended: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
}

interface Props {
  tenant: TenantSummary
  onClose: () => void
  onUpdated: () => void
}

export default function AdminCompanyDetail({ tenant, onClose, onUpdated }: Props) {
  const { switchTenant } = useTenant()
  const [editForm, setEditForm] = useState({
    name: tenant.name,
    plan: tenant.plan,
    status: tenant.status,
    domain: tenant.domain || '',
  })
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Reset form when tenant prop changes (e.g. switching companies)
  useEffect(() => {
    setEditForm({
      name: tenant.name,
      plan: tenant.plan,
      status: tenant.status,
      domain: tenant.domain || '',
    })
    setHasChanges(false)
  }, [tenant.tenant_id])

  function handleFieldChange(field: string, value: string) {
    setEditForm((f) => ({ ...f, [field]: value }))
    setHasChanges(true)
  }

  async function handleSave() {
    setSaving(true)
    try {
      await updateTenantById(tenant.tenant_id, {
        name: editForm.name,
        plan: editForm.plan,
        status: editForm.status,
        domain: editForm.domain || undefined,
      } as Partial<TenantSummary>)
      setHasChanges(false)
      toast.success('Company updated')
      onUpdated()
    } catch (err: any) {
      toast.error(err.message || 'Failed to update company')
    } finally {
      setSaving(false)
    }
  }

  function handleSwitchTo() {
    switchTenant(tenant.tenant_id)
    onClose()
  }

  const usagePercent = Math.min(100, ((tenant.usage?.messages_used || 0) / 10000) * 100)

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Slide-in Panel */}
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{tenant.name}</h2>
              <p className="text-xs text-gray-400 font-mono">{tenant.tenant_id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Badges */}
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full uppercase ${PLAN_COLORS[tenant.plan] || PLAN_COLORS.free}`}>
              {tenant.plan}
            </span>
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[tenant.status] || STATUS_COLORS.active}`}>
              {tenant.status}
            </span>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <Users className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900">{Object.keys(tenant.user_roles || {}).length + 1}</p>
              <p className="text-[10px] text-gray-500 uppercase">Users</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <MessageSquare className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900">{(tenant.usage?.messages_used || 0).toLocaleString()}</p>
              <p className="text-[10px] text-gray-500 uppercase">Messages</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <MessageSquare className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900">{(tenant.usage?.conversations_used || 0).toLocaleString()}</p>
              <p className="text-[10px] text-gray-500 uppercase">Convos</p>
            </div>
          </div>

          {/* Usage Bar */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-500">Message Usage</span>
              <span className="font-medium text-gray-700">{usagePercent.toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${usagePercent > 80 ? 'bg-red-400' : 'bg-amber-400'}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>

          {/* Editable Fields */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Company Settings</h3>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Company Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Plan</label>
                <select
                  value={editForm.plan}
                  onChange={(e) => handleFieldChange('plan', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white"
                >
                  <option value="free">Free</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white"
                >
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Domain</label>
              <input
                type="text"
                value={editForm.domain}
                onChange={(e) => handleFieldChange('domain', e.target.value)}
                placeholder="company.com"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Owner Email</label>
              <input
                type="text"
                value={tenant.admin_email}
                disabled
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>

            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Team Members */}
          <AdminUserManager tenantId={tenant.tenant_id} />

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Actions */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Actions</h3>
            <button
              onClick={handleSwitchTo}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-amber-900 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Switch to this Company
            </button>
          </div>

          {/* Danger Zone */}
          <div className="border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h4 className="text-sm font-semibold text-red-700">Danger Zone</h4>
            </div>
            <p className="text-xs text-red-600 mb-3">Suspending or deleting a company will prevent all its users from accessing the platform.</p>
            <div className="flex gap-2">
              {tenant.status !== 'suspended' && (
                <button
                  onClick={() => handleFieldChange('status', 'suspended')}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                >
                  Suspend Company
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
